import { useMemo } from "react";
import dayjs from "dayjs";
import { Typography } from "antd";
import { resolveTemplate } from "../../Utils/resolveTemplate";
import { useSlicerMenuRows, type SlicerMenuRow } from "./useSlicerTokenTrigger";

const { Text } = Typography;

export const FORMAT_TOKENS = [
  { token: "YYYY", desc: "4-digit year",       dayjsFmt: "YYYY" },
  { token: "YY",   desc: "2-digit year",       dayjsFmt: "YY"   },
  { token: "M",    desc: "Month number",       dayjsFmt: "M"    },
  { token: "MM",   desc: "Padded month",       dayjsFmt: "MM"   },
  { token: "MMM",  desc: "Short month name",   dayjsFmt: "MMM"  },
  { token: "MMMM", desc: "Full month name",    dayjsFmt: "MMMM" },
  { token: "D",    desc: "Day of month",       dayjsFmt: "D"    },
  { token: "DD",   desc: "Padded day",         dayjsFmt: "DD"   },
  { token: "DDD",  desc: "Short day of week",  dayjsFmt: "ddd"  },
  { token: "DDDD", desc: "Full day of week",   dayjsFmt: "dddd" },
];

export const VARIABLE_ROWS = [
  { display: "{CurrentDate}",  key: "CurrentDate",  label: "Today's date"  },
  { display: "{CurrentMonth}", key: "CurrentMonth", label: "Current month" },
  { display: "{CurrentYear}",  key: "CurrentYear",  label: "Current year"  },
];

const OFFSET_TEMPLATES = [
  { token: "{CurrentDate - 7}",  label: "7 days ago"  },
  { token: "{CurrentMonth + 1}", label: "Next month"  },
  { token: "{CurrentYear - 1}",  label: "Last year"   },
];

const tdToken: React.CSSProperties = {
  padding: "2px 6px 2px 0",
  fontFamily: "monospace",
  fontSize: 11,
  color: "#1a73e8",
  whiteSpace: "nowrap",
};
const tdClickable: React.CSSProperties = { ...tdToken, cursor: "pointer", textDecoration: "underline dotted" };
const tdLabel: React.CSSProperties = { padding: "2px 6px", color: "#555", fontSize: 11 };
const tdExample: React.CSSProperties = { padding: "2px 0", color: "#888", fontSize: 11 };
const tdBlank: React.CSSProperties = { ...tdExample, fontStyle: "italic" };
const slicerHead: React.CSSProperties = { display: "flex", alignItems: "center", gap: 6, margin: "6px 0 2px", fontSize: 11 };
const slicerKey: React.CSSProperties = { fontFamily: "monospace", color: "#1a73e8" };
const slicerKind: React.CSSProperties = { marginLeft: "auto", color: "#8a94a0" };

interface VariableSyntaxHelpProps {
  /** Receives the complete token text, `{CurrentDate}` or `{Slicer.WEEK.StartDate}`. */
  onInsert: (tokenText: string) => void;
  description?: string;
}

function SlicerBlock({ row, onInsert }: { row: SlicerMenuRow; onInsert: (tokenText: string) => void }) {
  return (
    <div>
      <div style={slicerHead}>
        <span className="slicer-badge-disc" style={{ background: row.color }} />
        <Text strong style={{ fontSize: 11 }}>{row.label}</Text>
        <span style={slicerKey}>{row.key}</span>
        <span style={slicerKind}>{row.kindLabel}</span>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          {row.variables.map((variable) => (
            <tr key={variable.name}>
              <td style={tdClickable} onClick={() => onInsert(variable.token)} title="Click to insert">
                {variable.token}
              </td>
              <td style={tdLabel}>{variable.meaning}</td>
              <td style={variable.blank ? tdBlank : tdExample}>{variable.text}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function VariableSyntaxHelp({
  onInsert,
  description = "Click a variable to insert it at cursor position.",
}: VariableSyntaxHelpProps) {
  const now = dayjs();
  const slicerRows = useSlicerMenuRows();
  const offsetExamples = useMemo(
    () => OFFSET_TEMPLATES.map((r) => ({ ...r, example: resolveTemplate(r.token).resolved })),
    [],
  );

  return (
    <div style={{ maxWidth: 400, maxHeight: 360, overflowY: "auto", fontSize: 12 }}>
      <Text strong style={{ fontSize: 12 }}>Dynamic Variables</Text>
      <p style={{ margin: "4px 0 8px", color: "#555", fontSize: 11 }}>{description}</p>
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 10 }}>
        <tbody>
          {VARIABLE_ROWS.map(({ display, key, label }) => (
            <tr key={key}>
              <td style={tdClickable} onClick={() => onInsert(display)} title="Click to insert">
                {display}
              </td>
              <td style={tdLabel}>{label}</td>
              <td style={tdExample}>{resolveTemplate(display).resolved}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <Text strong style={{ fontSize: 11 }}>Slicers</Text>
      {slicerRows.length === 0 ? (
        <p style={{ margin: "4px 0 10px", color: "#555", fontSize: 11 }}>
          No slicers on this board. Add a Slicer widget to quote its dates or athletes here.
        </p>
      ) : (
        <div style={{ marginBottom: 10 }}>
          {slicerRows.map((row) => (
            <SlicerBlock key={row.key} row={row} onInsert={onInsert} />
          ))}
        </div>
      )}

      <Text strong style={{ fontSize: 11 }}>Offsets</Text>
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 4, marginBottom: 10 }}>
        <tbody>
          {offsetExamples.map(({ token, label, example }) => (
            <tr key={token}>
              <td style={tdToken}>{token}</td>
              <td style={tdLabel}>{label}</td>
              <td style={tdExample}>{example}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <Text strong style={{ fontSize: 11 }}>Format Tokens</Text>
      <p style={{ margin: "4px 0 6px", color: "#555", fontSize: 11 }}>
        Use after a colon: <code style={{ fontSize: 10 }}>{"{CurrentDate:MMMM D, YYYY}"}</code>
      </p>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          {FORMAT_TOKENS.map(({ token, desc, dayjsFmt }) => (
            <tr key={token}>
              <td style={tdToken}>{token}</td>
              <td style={tdLabel}>{desc}</td>
              <td style={tdExample}>{now.format(dayjsFmt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
